import Rollbar from 'rollbar';
import type { Level } from 'rollbar';
import { each, filter, get, isArray, isEmpty, set } from 'lodash';

import getEnvironment, { getCurrentVersion, isEnvironment } from '@flow/env';

import { appendErrorContext, getRollbarUrl, sanitizePayloadByContext } from './helpers';
import { GUEST_TOKEN } from '../constants';

type TransformHelper = (payload: any) => void;

declare global {
  interface Window {
    rollbar?: any;
  }
}

const logCategories = {
  CKEditor: 'ckeditor',
  Pusher: 'push/dist/pusher',
  'Unknown error': 'script error',
};
const prefixCategory: TransformHelper = (trace) => {
  if (isEmpty(trace)) {
    return;
  }

  try {
    let framesContent = '';
    const stack = get(trace, 'extra.data.stack');

    if (isArray(trace.frames) && trace.frames.length) {
      framesContent = trace.frames.reduce(
        (
          content: string,
          frame: {
            filename?: string;
            method?: string;
          },
        ) => content + (frame.filename || '') + (frame.method || ''),
        '',
      );
    } else if (isArray(stack)) {
      framesContent = stack.join(' ');
    }

    each(logCategories, (pattern, category) => {
      // Check if the pattern matches
      if (!framesContent.toLowerCase().includes(pattern.toLowerCase())) {
        return;
      }

      const dataMessage = get(trace, 'extra.data.message');
      const exceptionMessage = get(trace, 'exception.message');
      const exceptionDescription = get(trace, 'exception.description');

      if (dataMessage) {
        set(trace, 'extra.data.message', `[${category}] ${trace.extra.data.message}`);
      }

      if (exceptionMessage) {
        set(trace, 'exception.message', `[${category}] ${exceptionMessage}`);
      }

      if (exceptionDescription) {
        set(trace, 'exception.description', `[${category}] ${exceptionDescription}`);
      }
    });
  } catch (e) {}
};

const scrubFields = [
  'value',
  'password',
  'password_again',
  'password_verification',
];

const ignoredMessages = [
  'ResizeObserver loop completed with undelivered notifications',
  'network error',
];

const transform: TransformHelper = (payload) => {
  appendErrorContext(payload);
  sanitizePayloadByContext(payload, GUEST_TOKEN);

  // Stack trace
  prefixCategory(get(payload, 'data.body.trace'));

  // if extra.data is not set on message, then to do nothing
  let extraData = get(payload, 'data.body.message.extra.data');
  if (!extraData) {
    return;
  }

  const basicMatches = filter(scrubFields, (field) => {
    const regexp = new RegExp(field, 'i');
    return regexp.test(extraData);
  });

  if (!basicMatches.length) {
    return;
  }

  each(basicMatches, (field) => {
    const regexp = new RegExp(`\\\\?["']${field}\\\\?["'] *: *\\\\?["'].*?\\\\?["']`, 'i');
    extraData = extraData.replace(regexp, `\\"${field}\\":\\"*****\\"`);
  });

  set(payload, 'data.body.message.extra.data', extraData);
};

// cache the blacklisted origins
const blackListedOrigins = [
  'chrome-extension://',
  'moz-extension://',
  'safari-web-extension://',
];

// cache the bot user agents, include other bots if needed
const botUserAgents = [
  'googlebot',
];

/**
 * Determines whether an error should be ignored.
 *
 * @param {boolean} _isUncaught - Indicates if the error is uncaught.
 * isUncaught is true if the error bubbled up to window.onerror.
 * It is false if the error came from one of the Rollbar.js logging methods. (Not used in this case)
 * @param {Array} args - Arguments containing error details.
 * @param {Object} payload - The Rollbar payload object.
 * @returns {boolean} True if the error's origin is blacklisted, otherwise false.
 */
export const shouldIgnoreError = (_isUncaught: boolean, args: Array<any>, payload: any) => {
  const errorOrigin = args[1]?.request?.headers?.origin;
  const errorUserAgent = payload?.client?.javascript?.browser;
  if (!navigator || !navigator.onLine) {
    // If the user is offline, ignore the error
    return true;
  }
  if (errorUserAgent && botUserAgents.some((bot) => errorUserAgent.toLowerCase().includes(bot))) {
    // If the user agent is a bot, ignore the error
    return true;
  }
  if (errorOrigin && blackListedOrigins.some((origin) => errorOrigin.startsWith(origin))) {
    // If the error originates from a blacklisted source (e.g., browser extension), ignore it
    return true;
  }
  return false;
};

const rollbarConfig = {
  scrubFields,
  transform,
  accessToken: '', // This will be set later in the init function
  captureUncaught: true,
  captureUnhandledRejections: false,
  checkIgnore: shouldIgnoreError,
  ignoredMessages,
  autoInstrument: true,
  reportLevel: 'error' as Level,
  endpoint: getRollbarUrl(),
  payload: {
    environment: getEnvironment(),
    client: {
      javascript: {
        source_map_enabled: true,
        guess_uncaught_frames: true,
        code_version: getCurrentVersion(),
      },
    },
  },
};

export const init = ({ accessToken }: { accessToken: string }) => {
  const rollbarInstance = Rollbar.init({
    ...rollbarConfig,
    accessToken,
  });

  rollbarInstance.global({
    itemsPerMinute: 300,
  });
  return rollbarInstance;
};

type Payload = {
  person: {
    id: string | number;
  };
};
type SetPayload = (payload: Payload) => any;
export const setPayload: SetPayload = ({ person }) => {
  if (!window.rollbar || isEnvironment('development')) {
    return {};
  }

  const settings = {
    payload: {
      client: {
        javascript: {
          source_map_enabled: true,
          guess_uncaught_frames: true,
          code_version: getCurrentVersion(),
        },
      },
      environment: getEnvironment(),
      person: {
        id: person.id,
        username: person.id,
      },
    },
  };

  return Rollbar.configure(settings);
};

export default {
  init,
  setPayload,
  transform,
};
